import cx from "classnames";

/**
 * One shared interaction language for every clickable/selectable "box" surface
 * in carrie. The canonical look comes from the ticket-grid tiles
 * (`routes/tickets/components/TicketGrid.tsx`) and the Ask Puntt skill/workflow
 * buttons (`routes/ask-puntt/components/WorkflowCards.tsx`): outline change on
 * hover/selected, a subtle background shift, a gentle upward lift with a soft
 * shadow, and icons that grow a touch on hover.
 *
 * The treatment here matches the strength of those originals so the hover is
 * clearly perceptible: a full upward lift, a large soft shadow, a clear
 * outline + background shift, and icons that grow on hover. It honors
 * `prefers-reduced-motion` by dropping the transform.
 */
interface InteractiveSurfaceVariant {
  /** Base hover treatment: outline + subtle bg shift + gentle lift + soft shadow. */
  interactive?: boolean;
  /** Selectable card semantics (pairs with `aria-pressed`); enables hover + selected. */
  selectable?: boolean;
  /** Distinct, stronger treatment when the surface is selected. */
  selected?: boolean;
  /** Accordion/expandable row: same outline/bg cue without the upward lift. */
  expandable?: boolean;
  /** Lighter resting border (neutral-4) on a white fill — review checklist chrome. */
  lightBorder?: boolean;
  /** Muted status panel: transparent shell — in-stream widgets (AgentSteps, SearchProgress). */
  muted?: boolean;
  /** Nested status row radius (rounded-lg) instead of the default rounded-xl shell. */
  compact?: boolean;
  /** Non-interactive / disabled affordance. */
  disabled?: boolean;
}

const baseSurface = (compact?: boolean) =>
  `group ${compact ? "rounded-lg" : "rounded-xl"} border transition-all duration-200 ease-out motion-reduce:transition-none`;

const RESTING = "border-puntt-neutral-gray-6 bg-base-white shadow-sm";
const RESTING_LIGHT_BORDER =
  "border-puntt-neutral-gray-4 bg-base-white shadow-sm";
// why: same bare convention as EnterpriseImpact / FeedMemoryCard — no fill or stroke.
const RESTING_MUTED = "border-transparent bg-transparent";

/** Lift treatment, matching the ticket/workflow originals so it's clearly visible. */
const HOVER_LIFT =
  "hover:-translate-y-1 hover:border-puntt-accent-7 hover:bg-puntt-blue-2 hover:shadow-lg motion-reduce:hover:translate-y-0";

const HOVER_FLAT_MUTED =
  "hover:border-puntt-accent-7 hover:bg-puntt-neutral-gray-2 hover:shadow-lg";

const HOVER_FLAT_LIGHT =
  "hover:border-puntt-accent-7 hover:bg-base-white hover:shadow-lg";

/** Selected reads clearly distinct from a plain hover: accent fill + ring + raised. */
const SELECTED =
  "-translate-y-1 border-puntt-accent-9 bg-puntt-accent-3 shadow-lg ring-2 ring-puntt-accent-9/50 motion-reduce:translate-y-0";

/**
 * Returns the unified surface className string for the requested variant. Use
 * this when you need to apply the treatment to an existing element (e.g. a
 * `<button>` already wired with handlers and testids) without restructuring it.
 */
export function interactiveSurfaceClasses({
  interactive,
  selectable,
  selected,
  expandable,
  lightBorder,
  muted,
  compact,
  disabled,
}: InteractiveSurfaceVariant = {}): string {
  const wantsLift = (interactive || selectable) && !expandable;
  const resting = muted
    ? RESTING_MUTED
    : lightBorder
      ? RESTING_LIGHT_BORDER
      : RESTING;
  const hoverFlat = lightBorder
    ? HOVER_FLAT_LIGHT
    : muted
      ? undefined
      : HOVER_FLAT_MUTED;
  return cx(baseSurface(compact), {
    [resting]: !selected,
    [SELECTED]: !!selected,
    [HOVER_LIFT]: wantsLift && !selected && !disabled,
    ...(hoverFlat ? { [hoverFlat]: !!expandable && !disabled } : {}),
    "cursor-pointer": (interactive || selectable || expandable) && !disabled,
    "cursor-default opacity-75": !!disabled,
  });
}

/**
 * Icon treatment that pairs with the surface: grows on hover (matching the
 * workflow buttons) and stays put under reduced-motion. Apply to the icon
 * wrapper inside an `InteractiveSurface` (which carries the `group`).
 */
export function interactiveSurfaceIconClasses(extra?: string): string {
  return cx(
    "transition-transform duration-200 group-hover:scale-105 motion-reduce:transform-none motion-reduce:group-hover:scale-100",
    extra,
  );
}
